<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TodoAlarm } from '@/api'
import { ALARM_PRESETS, describeAlarm } from '@/todos/alarms'
import { fromInputs } from '@/todos/dates'

const { t } = useI18n()
const props = defineProps<{ modelValue: TodoAlarm[]; disabled?: boolean; hasDate: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: TodoAlarm[]] }>()

const choice = ref('')
const customDate = ref('')
const customTime = ref('09:00')

function add(): void {
  let alarm: TodoAlarm | null = null
  if (choice.value === 'custom') {
    const at = fromInputs(customDate.value, customTime.value)
    if (at) alarm = { trigger: at, related: null, description: null }
  } else if (choice.value) {
    alarm = { trigger: choice.value, related: 'END', description: null }
  }
  if (!alarm) return
  emit('update:modelValue', [...props.modelValue, alarm])
  choice.value = ''
}

function remove(index: number): void {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  )
}
</script>

<template>
  <div class="alarms">
    <ul v-if="modelValue.length" class="list">
      <li v-for="(alarm, index) in modelValue" :key="index">
        <span>⏰ {{ describeAlarm(alarm) }}</span>
        <button
          v-if="!disabled"
          type="button"
          class="ui-btn ui-btn-icon"
          :aria-label="t('alarm.remove')"
          @click="remove(index)"
        >
          ✕
        </button>
      </li>
    </ul>
    <div v-if="!disabled" class="add">
      <select v-model="choice" class="ui-select">
        <option value="">{{ t('alarm.addPlaceholder') }}</option>
        <option
          v-for="preset in ALARM_PRESETS"
          :key="preset.trigger"
          :value="preset.trigger"
          :disabled="!hasDate"
        >
          {{ t(preset.labelKey) }}
        </option>
        <option value="custom">{{ t('alarm.custom') }}</option>
      </select>
      <template v-if="choice === 'custom'">
        <input v-model="customDate" type="date" class="ui-input" />
        <input v-model="customTime" type="time" class="ui-input" />
      </template>
      <button v-if="choice" type="button" class="ui-btn ui-btn-primary" @click="add">
        {{ t('common.add') }}
      </button>
    </div>
    <p v-if="!hasDate" class="ui-hint">{{ t('alarm.needsDue') }}</p>
  </div>
</template>

<style scoped>
.alarms {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.3rem 0;
}

.list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.add {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.add .ui-select {
  flex: 1 1 12rem;
}

.add .ui-input {
  flex: 1 1 7rem;
}

p {
  margin: 0;
}
</style>
